// client/src/services/trackingService.ts

import { apiClient } from './api';

// Types pour le tracking GPS
export interface VehicleLocation {
  id: string;
  vehicle_id: string;
  driver_id: string | null;
  mission_id: string | null;
  tenant_id: string;
  latitude: number;
  longitude: number;
  altitude?: number;
  accuracy?: number;
  heading?: number;
  speed?: number;
  address?: string;
  status: 'moving' | 'stopped' | 'idle';
  engine_status: 'on' | 'off' | 'unknown';
  fuel_level?: number;
  recorded_at: string;
  created_at: string;

  // Enriched data from view
  license_plate?: string;
  brand?: string;
  model?: string;
  vehicle_type?: string;
  vehicle_status?: string;
  driver_license?: string;
  driver_first_name?: string;
  driver_last_name?: string;
  driver_phone?: string;
  mission_number?: string;
  mission_status?: string;
  pickup_location?: string;
  delivery_location?: string;

  // Computed fields
  is_recent?: boolean;
  minutes_ago?: number;
}

export interface LocationHistory {
  vehicle: {
    id: string;
    license_plate: string;
    brand: string;
    model: string;
  };
  period: {
    start: string;
    end: string;
  };
  statistics: {
    total_points: number;
    avg_speed: string;
    max_speed: string;
    total_distance: string;
    days_tracked: number;
    first_recorded: string;
    last_recorded: string;
  };
  locations: VehicleLocation[];
}

export interface VehicleRoute {
  vehicle_id: string;
  license_plate?: string;
  has_tracking: boolean;
  route: RoutePoint[];
  statistics: {
    total_distance: string;
    total_points: number;
    duration_minutes: number;
    start_time: string;
    end_time: string;
  };
}

export interface MissionRoute {
  mission_id: string;
  vehicle_id?: string;
  has_tracking: boolean;
  route: RoutePoint[];
  statistics: {
    total_distance: string;
    total_points: number;
    duration_minutes: number;
    start_time: string;
    end_time: string;
  };
}

export interface RoutePoint {
  latitude: number;
  longitude: number;
  speed?: number;
  heading?: number;
  recorded_at: string;
  distance_from_previous?: number;
  distance_from_start?: number;
}

export interface LocationRecord {
  vehicle_id: string;
  driver_id?: string;
  mission_id?: string;
  latitude: number;
  longitude: number;
  altitude?: number;
  accuracy?: number;
  heading?: number;
  speed?: number;
  address?: string;
  status?: 'moving' | 'stopped' | 'idle';
  engine_status?: 'on' | 'off' | 'unknown';
  fuel_level?: number;
  recorded_at?: string;
}

export interface TrackingStats {
  total_vehicles: number;
  actively_tracked: number;
  in_mission: number;
  moving: number;
  stopped: number;
  idle: number;
  last_updated: string;
}

export interface VehicleLocationFilters {
  vehicle_status?: 'active' | 'inactive' | 'maintenance' | 'out_of_service';
  status?: 'moving' | 'stopped' | 'idle';
  has_mission?: boolean;
}

/**
 * Service pour la gestion du tracking GPS
 */
export class TrackingService {
  private readonly baseUrl = '/admin/tracking';

  /**
   * Récupérer les dernières positions de tous les véhicules
   */
  async getVehicleLocations(filters: VehicleLocationFilters = {}): Promise<{
    data: VehicleLocation[];
    count: number;
    timestamp: string;
  }> {
    const url = apiClient.buildUrl(this.baseUrl + '/vehicles', filters);
    return apiClient.get<{
      data: VehicleLocation[];
      count: number;
      timestamp: string;
    }>(url);
  }

  /**
   * Récupérer l'historique de localisation d'un véhicule
   */
  async getVehicleHistory(
    vehicleId: string,
    startDate?: string,
    endDate?: string
  ): Promise<LocationHistory> {
    const params: Record<string, string> = {};
    if (startDate) params.start = startDate;
    if (endDate) params.end = endDate;

    const url = apiClient.buildUrl(`${this.baseUrl}/vehicles/${vehicleId}/history`, params);
    return apiClient.get<LocationHistory>(url);
  }

  /**
   * Récupérer le parcours d'un véhicule
   */
  async getVehicleRoute(
    vehicleId: string,
    startTime?: string,
    endTime?: string
  ): Promise<VehicleRoute> {
    const params: Record<string, string> = {};
    if (startTime) params.start = startTime;
    if (endTime) params.end = endTime;

    const url = apiClient.buildUrl(`${this.baseUrl}/vehicles/${vehicleId}/route`, params);
    return apiClient.get<VehicleRoute>(url);
  }

  /**
   * Récupérer le parcours d'une mission
   */
  async getMissionRoute(missionId: string): Promise<MissionRoute> {
    return apiClient.get<MissionRoute>(`${this.baseUrl}/missions/${missionId}/route`);
  }

  /**
   * Récupérer les statistiques de tracking
   */
  async getTrackingStats(): Promise<TrackingStats> {
    return apiClient.get<TrackingStats>(`${this.baseUrl}/stats`);
  }

  /**
   * Enregistrer un point de localisation
   */
  async recordLocation(data: LocationRecord): Promise<{
    message: string;
    location: VehicleLocation;
    timestamp: string;
  }> {
    return apiClient.post<{
      message: string;
      location: VehicleLocation;
      timestamp: string;
    }>(`${this.baseUrl}/location`, data);
  }

  /**
   * Enregistrer plusieurs points de localisation en batch
   */
  async recordLocationsBatch(locations: LocationRecord[]): Promise<{
    message: string;
    count: number;
    locations: VehicleLocation[];
    timestamp: string;
  }> {
    return apiClient.post<{
      message: string;
      count: number;
      locations: VehicleLocation[];
      timestamp: string;
    }>(`${this.baseUrl}/locations/batch`, { locations });
  }

  /**
   * Nettoyer les anciennes données (admin uniquement)
   */
  async cleanupOldData(daysToKeep: number = 90): Promise<{
    message: string;
    deleted_count: number;
    days_kept: number;
    timestamp: string;
  }> {
    return apiClient.post<{
      message: string;
      deleted_count: number;
      days_kept: number;
      timestamp: string;
    }>(`${this.baseUrl}/cleanup`, { days_to_keep: daysToKeep });
  }
}

// Instance singleton du service
export const trackingService = new TrackingService();
